import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableSortLabel,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Refresh,
  AssignmentTurnedIn,
  PendingActions,
  Inventory2,
  Add,
  Delete,
  Download,
  UploadFile,
  KeyboardArrowDown,
  KeyboardArrowUp
} from '@mui/icons-material';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { ROLES } from '../constants/roles';
import { roleMatches } from '../utils/rolePermissions';
import MaintenanceWorkflowStatusCard from './maintenance/MaintenanceWorkflowStatusCard';
import PlanningActions from './maintenance/PlanningActions';
import { normalizePartNumber } from '../utils/partNumber';
import { getPlanningTask, isPendingPlanningTask } from '../utils/planningTasks';
import { needsStoreIntentAction } from '../utils/workOrderStoreReview';
import { getWorkOrderDisplayNumber, getWorkOrderCandidateNumber, CONTEXT } from '../utils/workOrderDisplayNumber';
import {
  HANGAR_JOB_TYPE_OPTIONS,
  HANGAR_ENVIRONMENT_OPTIONS
} from '../constants/maintenanceWorkOrderTypes';

const TAB = {
  DASHBOARD: 'dashboard',
  TASKS: 'tasks',
  PROCUREMENT: 'procurement'
};

const ALLOWED_ROLES = [ROLES.PLANNING_MANAGER, ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER];
const SORT_ORDER = {
  ASC: 'asc',
  DESC: 'desc'
};

const formatDate = (value) => {
  if (!value) return 'N/A';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'N/A';
  return d.toISOString().slice(0, 10);
};

const toDateKey = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
};

const Planning = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();
  const planningTabParam = searchParams.get('tab');
  const [tab, setTab] = useState(TAB.DASHBOARD);
  const [loading, setLoading] = useState(false);
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [planningActionOpen, setPlanningActionOpen] = useState(false);
  const [hangarProductMode, setHangarProductMode] = useState('SUITE');
  const [enableExternalJobs, setEnableExternalJobs] = useState(false);
  const [operators, setOperators] = useState([]);
  const [createHangarOpen, setCreateHangarOpen] = useState(false);
  const [createHangarForm, setCreateHangarForm] = useState({
    operatorId: '',
    aircraftId: '',
    registration: '',
    title: '',
    type: 'MAINTENANCE',
    maintenanceType: 'LINE_INSPECTION',
    priority: 'MEDIUM',
    externalJobId: '',
    needsStoreAction: true,
    pendingFiles: [],
    loading: false,
    error: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [priorityFilter, setPriorityFilter] = useState('ALL');
  const [taskTypeFilter, setTaskTypeFilter] = useState('ALL');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState(SORT_ORDER.DESC);
  const [expandedWorkflowId, setExpandedWorkflowId] = useState(null);
  const [independentPartNumbers, setIndependentPartNumbers] = useState(['']);
  const [independentQuantities, setIndependentQuantities] = useState(['']);
  const [independentDescriptions, setIndependentDescriptions] = useState(['']);
  const [independentAircraftId, setIndependentAircraftId] = useState('GEN');
  const [aircraftOptions, setAircraftOptions] = useState([]);
  const [independentNotes, setIndependentNotes] = useState('');
  const [independentSubmitting, setIndependentSubmitting] = useState(false);
  const [independentError, setIndependentError] = useState('');
  const [planningIntentRequests, setPlanningIntentRequests] = useState([]);
  const [planningIntentLoading, setPlanningIntentLoading] = useState(false);
  const [approvingIntentId, setApprovingIntentId] = useState('');
  const [partsCatalog, setPartsCatalog] = useState([]);
  const [partsCatalogLoading, setPartsCatalogLoading] = useState(false);
  const [inventoryAvailabilityMap, setInventoryAvailabilityMap] = useState({});
  const [inventoryAvailabilityLoading, setInventoryAvailabilityLoading] = useState(false);
  const independentCsvUploadRef = useRef(null);

  const canAccess = roleMatches(user?.role, ALLOWED_ROLES);
  const canApprovePlanningIntent = [ROLES.ACCOUNTABLE_MANAGER, ROLES.AM_MRO, ROLES.SUPER_ADMIN].includes(user?.role);

  const fetchWorkOrders = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/maintenance');
      setWorkOrders(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load planning page work orders:', err);
      setWorkOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAircraftOptions = async () => {
    try {
      const res = await axios.get('/api/aircraft');
      setAircraftOptions(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load aircraft options for planning procurement:', err);
      setAircraftOptions([]);
    }
  };

  const fetchPartsCatalog = async () => {
    setPartsCatalogLoading(true);
    try {
      const res = await axios.get('/api/parts', { params: { isActive: 'true', page: 1, pageSize: 500 } });
      const parts = Array.isArray(res.data?.parts) ? res.data.parts : (Array.isArray(res.data) ? res.data : []);
      setPartsCatalog(parts);
    } catch (err) {
      console.error('Failed to load parts catalog for planning procurement:', err);
      setPartsCatalog([]);
    } finally {
      setPartsCatalogLoading(false);
    }
  };

  const fetchPlanningIntentRequests = async () => {
    setPlanningIntentLoading(true);
    try {
      const res = await axios.get('/api/store/reorder-requests');
      const rows = Array.isArray(res.data) ? res.data : [];
      setPlanningIntentRequests(
        rows.filter((row) => row.createdFrom === 'PLANNING_NON_WO' && !row.isArchived)
      );
    } catch (err) {
      console.error('Failed to load planning independent intents:', err);
      setPlanningIntentRequests([]);
    } finally {
      setPlanningIntentLoading(false);
    }
  };

  useEffect(() => {
    if (canAccess) {
      fetchWorkOrders();
      fetchAircraftOptions();
      fetchPartsCatalog();
      fetchPlanningIntentRequests();
      axios.get('/api/mro/hangar-settings')
        .then((res) => {
          setHangarProductMode(res.data?.productMode || 'SUITE');
          setEnableExternalJobs(Boolean(res.data?.enableExternalJobs));
        })
        .catch(() => {
          setHangarProductMode('SUITE');
          setEnableExternalJobs(false);
        });
      axios.get('/api/master-data/operators', { params: { isActive: 'true' } })
        .then((res) => setOperators(Array.isArray(res.data) ? res.data : []))
        .catch(() => setOperators([]));
    }
  }, [canAccess]);

  const isHangarSku = hangarProductMode === 'HANGAR';
  const canCreateHangarJob = isHangarSku && enableExternalJobs;
  const displayContext = isHangarSku ? CONTEXT.MRO : CONTEXT.CAMO;

  const submitCreateHangarJob = async () => {
    if (!createHangarForm.operatorId) {
      setCreateHangarForm((prev) => ({ ...prev, error: 'Operator is required' }));
      return;
    }
    if (!createHangarForm.title.trim()) {
      setCreateHangarForm((prev) => ({ ...prev, error: 'Title is required' }));
      return;
    }
    if (!createHangarForm.aircraftId && !createHangarForm.registration.trim()) {
      setCreateHangarForm((prev) => ({ ...prev, error: 'Aircraft or registration is required' }));
      return;
    }
    setCreateHangarForm((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const { data } = await axios.post('/api/mro/packages/manual', {
        operatorId: createHangarForm.operatorId,
        externalJobId: createHangarForm.externalJobId.trim() || undefined,
        needsStoreAction: createHangarForm.needsStoreAction,
        aircraft: {
          aircraftId: createHangarForm.aircraftId || undefined,
          registration: createHangarForm.registration.trim() || undefined
        },
        task: {
          title: createHangarForm.title.trim(),
          type: createHangarForm.type,
          maintenanceType: createHangarForm.maintenanceType || undefined,
          priority: createHangarForm.priority
        }
      });

      const packageId = data?.package?._id;
      const pendingFiles = createHangarForm.pendingFiles || [];
      let attachmentFailures = 0;
      if (packageId && pendingFiles.length > 0) {
        for (const file of pendingFiles) {
          try {
            const fd = new FormData();
            fd.append('file', file);
            await axios.post(`/api/mro/packages/${packageId}/attachments`, fd, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } catch (uploadErr) {
            console.error('Hangar attachment upload failed', uploadErr);
            attachmentFailures += 1;
          }
        }
      }

      if (attachmentFailures > 0) {
        showToast(
          `Hangar job created, but ${attachmentFailures} attachment(s) failed to upload. Add them from Planning Actions.`,
          'warning'
        );
      } else {
        showToast(
          pendingFiles.length
            ? `Hangar job created with ${pendingFiles.length} attachment(s) — awaiting planning approval`
            : 'Hangar job created — awaiting planning approval',
          'success'
        );
      }
      setCreateHangarOpen(false);
      setCreateHangarForm({
        operatorId: '',
        aircraftId: '',
        registration: '',
        title: '',
        type: 'MAINTENANCE',
        maintenanceType: 'LINE_INSPECTION',
        priority: 'MEDIUM',
        externalJobId: '',
        needsStoreAction: true,
        pendingFiles: [],
        loading: false,
        error: ''
      });
      setTab(TAB.TASKS);
      fetchWorkOrders();
    } catch (err) {
      setCreateHangarForm((prev) => ({
        ...prev,
        loading: false,
        error: err.response?.data?.message || 'Failed to create hangar job'
      }));
    }
  };

  const handleHangarAttachmentPick = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setCreateHangarForm((prev) => ({
      ...prev,
      pendingFiles: [...(prev.pendingFiles || []), ...files]
    }));
    event.target.value = '';
  };

  const removePendingHangarAttachment = (index) => {
    setCreateHangarForm((prev) => ({
      ...prev,
      pendingFiles: (prev.pendingFiles || []).filter((_, i) => i !== index)
    }));
  };

  useEffect(() => {
    if (
      planningTabParam === TAB.DASHBOARD ||
      planningTabParam === TAB.TASKS ||
      planningTabParam === TAB.PROCUREMENT ||
      planningTabParam === 'pending'
    ) {
      setTab(planningTabParam === 'pending' ? TAB.TASKS : planningTabParam);
    }
  }, [planningTabParam]);

  const partDescriptionMap = useMemo(() => {
    const map = {};
    (partsCatalog || []).forEach((part) => {
      const key = String(part?.partNumber || '').trim().toLowerCase();
      if (!key || map[key]) return;
      map[key] = String(part?.description || part?.partName || '').trim();
    });
    return map;
  }, [partsCatalog]);

  const normalizedIndependentPartNumbers = useMemo(() => (
    Array.from(
      new Set(
        independentPartNumbers
          .map((pn) => normalizePartNumber(pn).toLowerCase())
          .filter(Boolean)
      )
    )
  ), [independentPartNumbers]);

  useEffect(() => {
    if (!normalizedIndependentPartNumbers.length) {
      setInventoryAvailabilityMap({});
      return;
    }

    let cancelled = false;
    const loadAvailability = async () => {
      setInventoryAvailabilityLoading(true);
      try {
        const responses = await Promise.all(
          normalizedIndependentPartNumbers.map((partNumber) => (
            axios.get('/api/store/inventory', {
              params: {
                status: 'IN_INVENTORY',
                search: partNumber,
                page: 1,
                pageSize: 500
              }
            })
          ))
        );

        if (cancelled) return;

        const liveMap = {};
        responses.forEach((response, index) => {
          const queryPartNumber = normalizedIndependentPartNumbers[index];
          const items = Array.isArray(response.data?.items)
            ? response.data.items
            : (Array.isArray(response.data) ? response.data : []);

          const exactMatchTotal = items.reduce((sum, item) => {
            const itemPartNumber = normalizePartNumber(item?.partNumber).toLowerCase();
            if (!itemPartNumber || itemPartNumber !== queryPartNumber) return sum;
            return sum + (Number(item?.quantity) || 0);
          }, 0);

          liveMap[queryPartNumber] = exactMatchTotal;
        });

        setInventoryAvailabilityMap(liveMap);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load planning independent inventory availability:', err);
          setInventoryAvailabilityMap({});
        }
      } finally {
        if (!cancelled) setInventoryAvailabilityLoading(false);
      }
    };

    loadAvailability();
    return () => { cancelled = true; };
  }, [normalizedIndependentPartNumbers]);

  const planningApproved = useMemo(
    () => workOrders.filter((wo) => Boolean(wo.planningApproved)),
    [workOrders]
  );

  const planningApprovalPending = useMemo(
    () =>
      workOrders.filter(
        (wo) => wo.camoQmAcknowledged && !wo.planningApproved && !wo.planningRejectedAt && wo.status !== 'CANCELLED'
      ),
    [workOrders]
  );

  const storeActionRequired = useMemo(
    () =>
      workOrders.filter(
        (wo) =>
          wo.planningApproved &&
          needsStoreIntentAction(wo) &&
          wo.status !== 'CANCELLED'
      ),
    [workOrders]
  );

  const planningTasks = useMemo(() => {
    return workOrders
      .map((wo) => ({ ...wo, planningTask: getPlanningTask(wo) }))
      .filter((wo) => Boolean(wo.planningTask))
      .filter((wo) => isPendingPlanningTask(wo));
  }, [workOrders]);

  const filteredAndSortedTasks = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filtered = planningTasks.filter((wo) => {
      const woLabel = `${wo.mroJobNumber || ''} ${wo.workOrderNumber || ''} ${wo.title || ''} ${wo.operatorCode || ''}`.toLowerCase();
      const aircraft = (wo.aircraftId?.registration || '').toLowerCase();
      const taskLabel = (wo.planningTask?.label || '').toLowerCase();
      const matchesSearch = !normalizedSearch ||
        woLabel.includes(normalizedSearch) ||
        aircraft.includes(normalizedSearch) ||
        taskLabel.includes(normalizedSearch);
      const matchesStatus = statusFilter === 'ALL' || (wo.status || 'UNKNOWN') === statusFilter;
      const matchesPriority = priorityFilter === 'ALL' || (wo.priority || 'MEDIUM') === priorityFilter;
      const matchesTaskType = taskTypeFilter === 'ALL' || wo.planningTask?.type === taskTypeFilter;
      return matchesSearch && matchesStatus && matchesPriority && matchesTaskType;
    });

    const getSortValue = (wo) => {
      if (sortBy === 'workOrder') return wo.workOrderNumber || wo._id || '';
      if (sortBy === 'title') return wo.title || '';
      if (sortBy === 'aircraft') return wo.aircraftId?.registration || '';
      if (sortBy === 'status') return wo.status || 'UNKNOWN';
      if (sortBy === 'task') return wo.planningTask?.label || '';
      if (sortBy === 'createdAt') return wo.createdAt || '';
      if (sortBy === 'dueDate') return toDateKey(wo.dueDate || wo.scheduledDate) || '';
      if (sortBy === 'scheduledDate') return toDateKey(wo.scheduledDate || wo.dueDate) || '';
      return '';
    };

    return [...filtered].sort((a, b) => {
      const aValue = getSortValue(a);
      const bValue = getSortValue(b);
      const comparison = typeof aValue === 'string'
        ? aValue.localeCompare(bValue)
        : aValue > bValue
          ? 1
          : aValue < bValue
            ? -1
            : 0;
      return sortOrder === SORT_ORDER.ASC ? comparison : comparison * -1;
    });
  }, [planningTasks, searchTerm, statusFilter, priorityFilter, taskTypeFilter, sortBy, sortOrder]);

  const availableStatuses = useMemo(
    () => ['ALL', ...new Set(planningTasks.map((wo) => wo.status || 'UNKNOWN'))],
    [planningTasks]
  );

  const availableTaskTypes = useMemo(
    () => ['ALL', ...new Set(planningTasks.map((wo) => wo.planningTask?.type).filter(Boolean))],
    [planningTasks]
  );
  const availablePriorities = useMemo(
    () => ['ALL', ...new Set(planningTasks.map((wo) => wo.priority || 'MEDIUM'))],
    [planningTasks]
  );

  const selectedTask = useMemo(
    () => filteredAndSortedTasks.find((task) => task._id === selectedTaskId) || filteredAndSortedTasks[0] || null,
    [selectedTaskId, filteredAndSortedTasks]
  );

  useEffect(() => {
    if (!selectedTask && filteredAndSortedTasks.length > 0) {
      setSelectedTaskId(filteredAndSortedTasks[0]._id);
    }
  }, [filteredAndSortedTasks, selectedTask]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === SORT_ORDER.ASC ? SORT_ORDER.DESC : SORT_ORDER.ASC));
      return;
    }
    setSortBy(column);
    setSortOrder(SORT_ORDER.ASC);
  };

  const handleAddIndependentPartRow = () => {
    setIndependentPartNumbers((prev) => [...prev, '']);
    setIndependentQuantities((prev) => [...prev, '']);
    setIndependentDescriptions((prev) => [...prev, '']);
  };

  const handleRemoveIndependentPartRow = (index) => {
    const nextPartNumbers = independentPartNumbers.filter((_, i) => i !== index);
    const nextQuantities = independentQuantities.filter((_, i) => i !== index);
    const nextDescriptions = independentDescriptions.filter((_, i) => i !== index);
    setIndependentPartNumbers(nextPartNumbers.length ? nextPartNumbers : ['']);
    setIndependentQuantities(nextQuantities.length ? nextQuantities : ['']);
    setIndependentDescriptions(nextDescriptions.length ? nextDescriptions : ['']);
  };

  const handleIndependentPartNumberChange = (index, value) => {
    setIndependentPartNumbers((prev) => {
      const next = [...prev];
      next[index] = normalizePartNumber(value);
      return next;
    });
    const normalized = String(value || '').trim().toLowerCase();
    const matchedDescription = normalized ? (partDescriptionMap[normalized] || '') : '';
    setIndependentDescriptions((prev) => {
      const next = [...prev];
      const current = String(next[index] || '').trim();
      if (!current || matchedDescription) {
        next[index] = matchedDescription;
      }
      return next;
    });
  };

  const handleIndependentQuantityChange = (index, value) => {
    setIndependentQuantities((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleIndependentDescriptionChange = (index, value) => {
    setIndependentDescriptions((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleSubmitIndependentProcurement = async () => {
    try {
      setIndependentSubmitting(true);
      setIndependentError('');

      const validRows = independentPartNumbers
        .map((partNumber, index) => ({
          partNumber: normalizePartNumber(partNumber),
          requestedQuantity: Number(independentQuantities[index]) || 1,
          description: String(independentDescriptions[index] || '').trim()
        }))
        .filter((row) => row.partNumber);

      if (!validRows.length) {
        setIndependentError('At least one part number is required.');
        return;
      }

      await Promise.all(validRows.map((row) => (
        axios.post('/api/store/reorder-requests', {
          partNumber: row.partNumber,
          requestedQuantity: row.requestedQuantity,
          description: row.description || undefined,
          aircraftId: independentAircraftId || 'GEN',
          intentType: 'PLANNING_NON_WO',
          urgency: 'MEDIUM',
          notes: independentNotes.trim() || 'Raised by Planning without WO'
        })
      )));

      showToast('Independent procurement request sent to procurement.', 'success');
      setIndependentPartNumbers(['']);
      setIndependentQuantities(['']);
      setIndependentDescriptions(['']);
      setIndependentAircraftId('GEN');
      setIndependentNotes('');
      setIndependentError('');
      fetchPlanningIntentRequests();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to send independent procurement request.';
      setIndependentError(message);
      showToast(message, 'error');
    } finally {
      setIndependentSubmitting(false);
    }
  };

  const handleApprovePlanningIntent = async (requestId) => {
    try {
      setApprovingIntentId(requestId);
      await axios.put(`/api/store/reorder-requests/${requestId}`, { status: 'APPROVED' });
      showToast('Planning intent approved. It is now available in Independent PO.', 'success');
      fetchPlanningIntentRequests();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to approve planning intent.';
      showToast(message, 'error');
    } finally {
      setApprovingIntentId('');
    }
  };

  const escapeCsv = (value) => {
    const stringValue = String(value ?? '');
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const parseCsvLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    result.push(current.trim());
    return result;
  };

  const handleDownloadIndependentCsv = () => {
    const rows = independentPartNumbers
      .map((partNumber, idx) => ({
        partNumber: String(partNumber || '').trim(),
        quantity: Number(independentQuantities[idx]) || '',
        description: String(independentDescriptions[idx] || '').trim()
      }))
      .filter((row) => row.partNumber);

    const csvLines = [
      ['Part Number', 'Quantity', 'Description'].join(','),
      ...rows.map((row) => [
        escapeCsv(row.partNumber),
        escapeCsv(row.quantity),
        escapeCsv(row.description)
      ].join(','))
    ];

    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'planning-independent-procurement.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const handleUploadIndependentCsvClick = () => {
    independentCsvUploadRef.current?.click();
  };

  const handleUploadIndependentCsv = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((line) => line.trim());
      if (lines.length < 2) {
        showToast('CSV has no data rows to import', 'error');
        return;
      }
      const header = parseCsvLine(lines[0]).map((h) => h.toLowerCase().trim());
      const pnIdx = header.findIndex((h) => h === 'part number' || h === 'partnumber' || h === 'pn');
      const qtyIdx = header.findIndex((h) => h === 'quantity' || h === 'qty');
      const descIdx = header.findIndex((h) => h === 'description' || h === 'desc');
      if (pnIdx < 0 || qtyIdx < 0) {
        showToast('CSV must contain "Part Number" and "Quantity" columns', 'error');
        return;
      }

      const importedRows = lines
        .slice(1)
        .map((line) => parseCsvLine(line))
        .map((columns) => {
          const partNumber = normalizePartNumber(columns[pnIdx]);
          const qtyRaw = Number(columns[qtyIdx]);
          const quantity = Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 1;
          const uploadedDescription = descIdx >= 0 ? String(columns[descIdx] || '').trim() : '';
          const knownDescription = partDescriptionMap[partNumber.toLowerCase()] || '';
          const description = knownDescription || uploadedDescription;
          return { partNumber, quantity, description };
        })
        .filter((row) => row.partNumber);

      if (!importedRows.length) {
        showToast('No valid part rows found in CSV', 'error');
        return;
      }

      setIndependentPartNumbers(importedRows.map((row) => row.partNumber).concat(['']));
      setIndependentQuantities(importedRows.map((row) => row.quantity).concat(['']));
      setIndependentDescriptions(importedRows.map((row) => row.description).concat(['']));
      showToast(`Imported ${importedRows.length} procurement row(s)`, 'success');
    } catch (err) {
      console.error(err);
      showToast('Failed to import CSV', 'error');
    } finally {
      event.target.value = '';
    }
  };

  const independentProcurementPreviewRows = independentPartNumbers
    .map((partNumber, index) => {
      const normalizedPart = normalizePartNumber(partNumber);
      const trimmedPart = String(normalizedPart || '').trim();
      const requiredQty = Number(independentQuantities[index]) || 0;
      if (!trimmedPart || requiredQty <= 0) return null;
      const partKey = trimmedPart.toLowerCase();
      const availableQty = Number(inventoryAvailabilityMap[partKey]) || 0;
      const remainingQty = Math.max(requiredQty - availableQty, 0);
      return {
        partNumber: trimmedPart,
        requiredQty,
        availableQty,
        remainingQty,
        description: String(independentDescriptions[index] || '').trim()
      };
    })
    .filter(Boolean);

  if (!canAccess) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>Planning</Typography>
        <Paper sx={{ p: 2 }}>
          <Typography>You do not have access to this page.</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Planning</Typography>
        <Stack direction="row" spacing={1}>
          {canCreateHangarJob && (
            <Button variant="contained" startIcon={<Add />} onClick={() => setCreateHangarOpen(true)}>
              New job card
            </Button>
          )}
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchWorkOrders} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Stack>
      </Box>

      {isHangarSku && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Hangar mode: jobs enter Planning first. Client portal submissions are requests — open the task
          and create the work order (assign Operator / schedule / Store-MRO flags). After that, one pipeline to Store/MRO.
        </Alert>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)}>
          <Tab value={TAB.DASHBOARD} label="Dashboard" />
          <Tab value={TAB.TASKS} label="Tasks" />
          <Tab value={TAB.PROCUREMENT} label="Independent PO Request" />
        </Tabs>
      </Paper>

      {tab === TAB.DASHBOARD && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <AssignmentTurnedIn color="success" />
                  <Typography variant="subtitle1">Approved</Typography>
                </Box>
                <Typography variant="h4">{planningApproved.length}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <PendingActions color="warning" />
                  <Typography variant="subtitle1">Pending</Typography>
                </Box>
                <Typography variant="h4">{planningApprovalPending.length}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Inventory2 color="info" />
                  <Typography variant="subtitle1">Store Action Required</Typography>
                </Box>
                <Typography variant="h4">{storeActionRequired.length}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {tab === TAB.TASKS && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Planning Tasks</Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} mb={2}>
                <TextField
                  size="small"
                  label="Search (WO #, title, aircraft, task)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  fullWidth
                />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    {availableStatuses.map((status) => (
                      <MenuItem key={status} value={status}>{status}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    label="Priority"
                    value={priorityFilter}
                    onChange={(e) => setPriorityFilter(e.target.value)}
                  >
                    {availablePriorities.map((priority) => (
                      <MenuItem key={priority} value={priority}>{priority}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel>Task Type</InputLabel>
                  <Select
                    label="Task Type"
                    value={taskTypeFilter}
                    onChange={(e) => setTaskTypeFilter(e.target.value)}
                  >
                    {availableTaskTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type === 'ALL' ? 'ALL' : type.replaceAll('_', ' ')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'workOrder'}
                        direction={sortBy === 'workOrder' ? sortOrder : SORT_ORDER.ASC}
                        onClick={() => handleSort('workOrder')}
                      >
                        Work Order
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'title'}
                        direction={sortBy === 'title' ? sortOrder : SORT_ORDER.ASC}
                        onClick={() => handleSort('title')}
                      >
                        Title
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'aircraft'}
                        direction={sortBy === 'aircraft' ? sortOrder : SORT_ORDER.ASC}
                        onClick={() => handleSort('aircraft')}
                      >
                        Aircraft
                      </TableSortLabel>
                    </TableCell>
                    {isHangarSku && <TableCell>Operator</TableCell>}
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'dueDate'}
                        direction={sortBy === 'dueDate' ? sortOrder : SORT_ORDER.ASC}
                        onClick={() => handleSort('dueDate')}
                      >
                        Due Date (CAMO)
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'scheduledDate'}
                        direction={sortBy === 'scheduledDate' ? sortOrder : SORT_ORDER.ASC}
                        onClick={() => handleSort('scheduledDate')}
                      >
                        Scheduled Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'status'}
                        direction={sortBy === 'status' ? sortOrder : SORT_ORDER.ASC}
                        onClick={() => handleSort('status')}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'task'}
                        direction={sortBy === 'task' ? sortOrder : SORT_ORDER.ASC}
                        onClick={() => handleSort('task')}
                      >
                        Task
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAndSortedTasks.map((wo) => (
                    <React.Fragment key={wo._id}>
                      <TableRow hover>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                            <span>
                              {getWorkOrderDisplayNumber(wo, displayContext)
                                || (wo.jobSource === 'EXTERNAL_PORTAL' ? (wo.externalJobId || 'Client request') : null)
                                || wo._id}
                            </span>
                            {wo.jobSource === 'EXTERNAL_PORTAL' && !wo.planningApproved && !getWorkOrderDisplayNumber(wo, displayContext) ? (
                              <Chip size="small" color="warning" variant="outlined" label="Client request" />
                            ) : null}
                            {getWorkOrderCandidateNumber(wo, displayContext) ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={isHangarSku
                                  ? `WO: ${getWorkOrderCandidateNumber(wo, displayContext)}`
                                  : `MRO: ${getWorkOrderCandidateNumber(wo, displayContext)}`}
                              />
                            ) : null}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {wo.title?.trim()
                            ? wo.title.trim()
                            : (
                              <Typography component="span" variant="body2" color="warning.main">
                                —
                              </Typography>
                            )}
                        </TableCell>
                        <TableCell>{wo.aircraftId?.registration || 'N/A'}</TableCell>
                        {isHangarSku && (
                          <TableCell>{wo.operatorCode || wo.operatorName || '—'}</TableCell>
                        )}
                        <TableCell>{formatDate(wo.dueDate || wo.scheduledDate)}</TableCell>
                        <TableCell>{formatDate(wo.scheduledDate || wo.dueDate)}</TableCell>
                        <TableCell>
                          <Chip size="small" label={wo.status || 'UNKNOWN'} />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Chip
                              size="small"
                              color={wo.planningTask?.color || 'default'}
                              label={wo.planningTask?.label || 'Task'}
                            />
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedTaskId(wo._id);
                                setExpandedWorkflowId((prev) => (prev === wo._id ? null : wo._id));
                              }}
                            >
                              {expandedWorkflowId === wo._id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                            </IconButton>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant={wo.planningTask?.type === 'CREATE_WORK_ORDER' ? 'contained' : 'outlined'}
                            onClick={() => {
                              setSelectedTaskId(wo._id);
                              setPlanningActionOpen(true);
                            }}
                          >
                            {wo.planningTask?.type === 'CREATE_WORK_ORDER' ? 'Create WO' : 'Open Task'}
                          </Button>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={isHangarSku ? 9 : 8} sx={{ py: 0, borderBottom: 0 }}>
                          <Collapse in={expandedWorkflowId === wo._id} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 1 }}>
                              <MaintenanceWorkflowStatusCard workOrder={wo} user={user} />
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                  {filteredAndSortedTasks.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={isHangarSku ? 9 : 8}>No planning tasks pending.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>
          </Grid>
        </Grid>
      )}

      {tab === TAB.PROCUREMENT && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Independent Procurement Request</Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Raise parts requests without WO linkage. Procurement receives them under the Independent PO tab and can create PO(s).
              </Alert>
              <Alert severity="info" sx={{ mb: 2 }}>
                Part descriptions auto-fill from parts catalog. Live inventory availability is shown per part number.
                {(partsCatalogLoading || inventoryAvailabilityLoading) ? ' Refreshing data...' : ''}
              </Alert>
              {independentError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {independentError}
                </Alert>
              ) : null}

              {independentPartNumbers.map((partNumber, index) => (
                <Grid container spacing={2} key={`${index}-${partNumber}`} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label={`Part Number ${index + 1}`}
                      value={partNumber}
                      onChange={(e) => handleIndependentPartNumberChange(index, e.target.value)}
                      placeholder="Enter part number"
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label={`Quantity ${index + 1}`}
                      value={independentQuantities[index] || ''}
                      onChange={(e) => handleIndependentQuantityChange(index, e.target.value)}
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      label={`Description ${index + 1}`}
                      value={independentDescriptions[index] || ''}
                      onChange={(e) => handleIndependentDescriptionChange(index, e.target.value)}
                      placeholder="Part description"
                    />
                  </Grid>
                  <Grid item xs={12} md={1}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: '100%' }}>
                      {independentPartNumbers.length > 1 ? (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveIndependentPartRow(index)}
                        >
                          <Delete />
                        </IconButton>
                      ) : null}
                      {index === independentPartNumbers.length - 1 ? (
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={handleAddIndependentPartRow}
                        >
                          <Add />
                        </IconButton>
                      ) : null}
                    </Box>
                  </Grid>
                </Grid>
              ))}

              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={handleDownloadIndependentCsv}
                  disabled={independentPartNumbers.filter((pn) => pn.trim()).length === 0}
                >
                  Download CSV
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<UploadFile />}
                  onClick={handleUploadIndependentCsvClick}
                >
                  Upload CSV
                </Button>
                <input
                  type="file"
                  ref={independentCsvUploadRef}
                  onChange={handleUploadIndependentCsv}
                  accept=".csv,text/csv"
                  style={{ display: 'none' }}
                />
              </Box>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Reason / Notes for Procurement (Optional)"
                value={independentNotes}
                onChange={(e) => setIndependentNotes(e.target.value)}
                sx={{ mb: 2 }}
              />

              {independentProcurementPreviewRows.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Inventory Status (Live Preview)
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Part Number</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Required</TableCell>
                        <TableCell align="right">Available</TableCell>
                        <TableCell align="right">Remaining</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {independentProcurementPreviewRows.map((row) => (
                        <TableRow key={`${row.partNumber}-${row.requiredQty}`}>
                          <TableCell>{row.partNumber}</TableCell>
                          <TableCell>{row.description || '—'}</TableCell>
                          <TableCell align="right">{row.requiredQty}</TableCell>
                          <TableCell align="right">{row.availableQty}</TableCell>
                          <TableCell align="right">{row.remainingQty}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Aircraft</InputLabel>
                <Select
                  label="Aircraft"
                  value={independentAircraftId}
                  onChange={(e) => setIndependentAircraftId(e.target.value)}
                >
                  <MenuItem value="GEN">GEN (general stock)</MenuItem>
                  {aircraftOptions.map((aircraft) => (
                    <MenuItem key={aircraft._id} value={aircraft._id}>
                      {aircraft.registration} {aircraft.model ? `- ${aircraft.model}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                color="secondary"
                onClick={handleSubmitIndependentProcurement}
                disabled={independentSubmitting || independentPartNumbers.filter((pn) => pn.trim()).length === 0}
              >
                {independentSubmitting ? 'Sending...' : 'Raise Independent Procurement Request'}
              </Button>

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Planning Intents for AM Approval
                </Typography>
                {planningIntentLoading ? (
                  <Typography variant="body2" color="text.secondary">Loading intents...</Typography>
                ) : planningIntentRequests.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No independent planning intents found.</Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Part</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell>Aircraft</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {planningIntentRequests.map((row) => (
                        <TableRow key={row._id}>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{row.partNumber}</TableCell>
                          <TableCell>{row.description || '—'}</TableCell>
                          <TableCell align="right">{row.requestedQuantity}</TableCell>
                          <TableCell>{row.requestedAircraftLabel || row.requestedAircraftId?.registration || 'GEN'}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={row.status === 'APPROVED' ? 'success' : 'warning'}
                              label={row.status || 'PENDING'}
                            />
                          </TableCell>
                          <TableCell>{row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '—'}</TableCell>
                          <TableCell align="right">
                            {canApprovePlanningIntent && row.status === 'PENDING' ? (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleApprovePlanningIntent(row._id)}
                                disabled={approvingIntentId === row._id}
                              >
                                {approvingIntentId === row._id ? 'Approving...' : 'AM Approve'}
                              </Button>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                {row.status === 'APPROVED' ? 'Sent to procurement intent' : 'Awaiting AM approval'}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      <PlanningActions
        open={planningActionOpen}
        onClose={() => setPlanningActionOpen(false)}
        workOrder={selectedTask}
        onSuccess={() => {
          setPlanningActionOpen(false);
          fetchWorkOrders();
        }}
      />

      <Dialog
        open={createHangarOpen}
        onClose={() => !createHangarForm.loading && setCreateHangarOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>New job card</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel id="hangar-create-operator">Operator</InputLabel>
              <Select
                labelId="hangar-create-operator"
                label="Operator"
                value={createHangarForm.operatorId}
                onChange={(e) => setCreateHangarForm((prev) => ({ ...prev, operatorId: e.target.value }))}
              >
                {operators.map((op) => (
                  <MenuItem key={op._id} value={op._id}>
                    {op.code ? `${op.code} — ${op.name}` : op.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="hangar-create-aircraft">Aircraft</InputLabel>
              <Select
                labelId="hangar-create-aircraft"
                label="Aircraft"
                value={createHangarForm.aircraftId}
                onChange={(e) => setCreateHangarForm((prev) => ({ ...prev, aircraftId: e.target.value }))}
              >
                <MenuItem value="">— Enter registration below —</MenuItem>
                {aircraftOptions.map((ac) => (
                  <MenuItem key={ac._id} value={ac._id}>
                    {ac.registration} {ac.model ? `(${ac.model})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {!createHangarForm.aircraftId && (
              <TextField
                label="Registration"
                fullWidth
                value={createHangarForm.registration}
                onChange={(e) => setCreateHangarForm((prev) => ({ ...prev, registration: e.target.value }))}
              />
            )}
            <TextField
              label="Title"
              required
              fullWidth
              value={createHangarForm.title}
              onChange={(e) => setCreateHangarForm((prev) => ({ ...prev, title: e.target.value }))}
            />
            <TextField
              select
              label="Environment"
              fullWidth
              value={createHangarForm.maintenanceType}
              onChange={(e) => setCreateHangarForm((prev) => ({ ...prev, maintenanceType: e.target.value }))}
              helperText="Same Line / Base options as AMP Schedules"
            >
              {HANGAR_ENVIRONMENT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Type"
              fullWidth
              value={createHangarForm.type}
              onChange={(e) => setCreateHangarForm((prev) => ({ ...prev, type: e.target.value }))}
              helperText="Includes AMP schedule inspection categories"
            >
              {HANGAR_JOB_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="External job ID (optional)"
              fullWidth
              value={createHangarForm.externalJobId}
              onChange={(e) => setCreateHangarForm((prev) => ({ ...prev, externalJobId: e.target.value }))}
              helperText="Reference from the outside CAMO / operator system"
            />
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Attachments
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                PDF, DOC, DOCX, XLS, XLSX, JPG, PNG, TXT, CSV, ZIP (max 50 MB each)
              </Typography>
              {(createHangarForm.pendingFiles || []).length > 0 && (
                <Stack spacing={0.5} sx={{ mb: 1 }}>
                  {createHangarForm.pendingFiles.map((file, index) => (
                    <Stack
                      key={`${file.name}-${index}`}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                        {file.name}
                      </Typography>
                      <IconButton
                        size="small"
                        aria-label="Remove attachment"
                        onClick={() => removePendingHangarAttachment(index)}
                        disabled={createHangarForm.loading}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
              )}
              <Button
                variant="outlined"
                size="small"
                component="label"
                startIcon={<UploadFile />}
                disabled={createHangarForm.loading}
              >
                Add attachment
                <input
                  type="file"
                  hidden
                  multiple
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt,.csv,.zip"
                  onChange={handleHangarAttachmentPick}
                />
              </Button>
            </Box>
            {createHangarForm.error && <Alert severity="error">{createHangarForm.error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateHangarOpen(false)} disabled={createHangarForm.loading}>Cancel</Button>
          <Button variant="contained" onClick={submitCreateHangarJob} disabled={createHangarForm.loading}>
            {createHangarForm.loading ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Planning;
